import React, { useMemo, useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import Modal from "../common/Modal";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  Squares2X2Icon,
  DeviceTabletIcon,
  LinkSlashIcon,
  ClockIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import ScreenForm from "../forms/ScreenForm";
import UnassignDeviceModal from "../modals/UnassignDeviceModal";
import ScreenDeviceHistoryModal from "../modals/ScreenDeviceHistoryModal";
import DeviceLogsModal from "../modals/DeviceLogsModal";
import {
  Screen,
  ScreenFormData,
  ScreenColumnFilter,
  ScreenFilterField,
} from "../../schemas/screenSchema";
import { useScreensInfinite } from "../../hooks/useScreensInfinite";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { useScreenStatusSubscription } from "../../hooks/useScreenStatusSubscription";
import { formatLastOnDate, formatLastOnTime } from "../../utils/dateUtils";
import { getChangedFields } from "../../utils/formUtils";

const ORIENTATION_TABS: {
  label: string;
  value: "" | "landscape" | "portrait";
  icon: React.ReactNode;
}[] = [
  {
    label: "All",
    value: "",
    icon: <Squares2X2Icon className="h-5 w-5" />,
  },
  {
    label: "Landscape",
    value: "landscape",
    icon: <DeviceTabletIcon className="h-5 w-5 rotate-90" />,
  },
  {
    label: "Portrait",
    value: "portrait",
    icon: <DeviceTabletIcon className="h-5 w-5" />,
  },
];

const Screens: React.FC = () => {
  useScreenStatusSubscription();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<Screen | null>(null);
  const [unassignScreen, setUnassignScreen] = useState<Screen | null>(null);
  const [historyScreen, setHistoryScreen] = useState<Screen | null>(null);
  const [logsScreen, setLogsScreen] = useState<Screen | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [orientation, setOrientation] = useState<"" | "landscape" | "portrait">(
    "",
  );
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );

  const columnFilterArray = useMemo<ScreenColumnFilter[]>(
    () =>
      Object.entries(columnFilters)
        .filter(([, value]) => value && value.trim())
        .map(([field, value]) => ({
          field: field as ScreenFilterField,
          value: value.trim(),
        })),
    [columnFilters],
  );

  const {
    screens,
    isLoading: isScreensLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createScreen,
    updateScreen,
    resetScreen,
    deleteScreens,
  } = useScreensInfinite({
    orientation: orientation || undefined,
    columnFilters: columnFilterArray.length ? columnFilterArray : undefined,
  });
  const { canCreate, canDelete, canEdit, canView } =
    useAuthPermissions("screens");

  const handleEdit = (screen: Screen) => {
    setSelectedScreen(screen);
    setIsModalOpen(true);
  };

  const handleDelete = (screen: Screen) => {
    if (
      window.confirm(`Are you sure you want to delete ${screen.screenName}?`)
    ) {
      deleteScreens([screen.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(`Are you sure you want to delete ${ids.length} screen(s)?`)
    ) {
      deleteScreens(ids);
      setRowSelection({});
    }
  };

  const handleResetToggle = async (screen: Screen) => {
    try {
      await resetScreen(screen.id);
    } catch (error) {
      // Error is handled by toast notification
    }
  };

  const handleSubmit = async (data: ScreenFormData) => {
    setIsLoading(true);
    try {
      if (selectedScreen) {
        const changedFields = getChangedFields(data, {
          screenName: selectedScreen.screenName,
          debugFlag: selectedScreen.debugFlag,
          resetFlag: selectedScreen.resetFlag,
          status: selectedScreen.status,
          layout: selectedScreen.layout,
          screenSaver: selectedScreen.screenSaver,
          address: selectedScreen.address,
          latitude: selectedScreen.latitude,
          longitude: selectedScreen.longitude,
          regionId: selectedScreen.regionId,
          restaurantId: selectedScreen.restaurantId,
          tickerId: selectedScreen.tickerId,
          widgetId: selectedScreen.widgetId,
          primeEndTime: selectedScreen.primeEndTime,
          primeStartTime: selectedScreen.primeStartTime,
          orientation: selectedScreen.orientation,
          tagIds: selectedScreen.tagIds,
          verificationCode: selectedScreen.verificationCode || "",
        });

        await updateScreen({
          id: selectedScreen.id as string,
          screen: changedFields,
        });
      } else {
        if (!data.verificationCode) {
          throw new Error("Verification code is required");
        }
        await createScreen({
          ...data,
          status: false,
          verificationCode: data.verificationCode,
        });
      }
      setIsModalOpen(false);
      setSelectedScreen(null);
    } catch (error) {
      // Error is handled by toast notification in the mutation hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedScreen(null);
  };

  const columns: ColumnDef<Screen>[] = [
    {
      accessorKey: "screenName",
      header: "Screen",
      meta: { filterKey: "SCREEN_NAME" },
    },
    {
      accessorKey: "restaurant.code",
      header: "Customer",
      meta: { filterKey: "CUSTOMER_ID" },
    },
    {
      accessorKey: "restaurant.name",
      header: "Restaurant",
      meta: { filterKey: "RESTAURANT_NAME" },
    },
    {
      accessorKey: "region.city.name",
      header: "City",
      meta: { filterKey: "CITY" },
    },
    {
      accessorKey: "region.name",
      header: "Region",
      meta: { filterKey: "REGION" },
    },
    {
      accessorKey: "orientation",
      header: "Orientation",
      meta: { filterKey: "ORIENTATION" },
      enableSorting: false,
    },

    {
      id: "status",
      header: "Status",
      meta: { filterKey: "STATUS", filterPlaceholder: "on / off" },
      cell: ({ row }) => {
        const screen = row.original;
        if (screen.status === null || screen.status === undefined) {
          return <span className="text-sm text-gray-500">-</span>;
        }
        const isOn = screen.status === true;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isOn ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
            }`}
          >
            {isOn ? "On" : "Off"}
          </span>
        );
      },
    },
    {
      id: "lastOnDate",
      header: "Last On Date",
      accessorKey: "lastOnAt",
      enableSorting: true,
      meta: { filterKey: "LAST_ON_DATE", filterPlaceholder: "dd-mm-yyyy" },
      cell: ({ row }) => {
        const screen = row.original;
        const dateTime = screen.lastOnAt || new Date().toISOString();
        return (
          <span className="text-sm text-gray-700">
            {formatLastOnDate(dateTime)}
          </span>
        );
      },
    },
    {
      id: "lastOnTime",
      header: "Last On Time",
      meta: { filterKey: "LAST_ON_TIME", filterPlaceholder: "hh:mm" },
      cell: ({ row }) => {
        const screen = row.original;
        const dateTime = screen.lastOnAt || new Date().toISOString();
        return (
          <span className="text-sm text-gray-700">
            {formatLastOnTime(dateTime)}
          </span>
        );
      },
    },
    {
      accessorKey: "verificationCode",
      header: "Device Code",
      meta: { filterKey: "DEVICE_CODE" },
      enableSorting: false,
    },
    {
      id: "reset",
      header: "Reset",
      cell: ({ row }) => {
        const screen = row.original;
        const isResetOn = screen.resetFlag || false;
        return (
          <button
            onClick={() => handleResetToggle(screen)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-default focus:ring-offset-2 ${
              isResetOn ? "bg-primary-default" : "bg-gray-300"
            }`}
            disabled={!canEdit}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isResetOn ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        );
      },
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Unassign Device",
      icon: <LinkSlashIcon className="h-4 w-4" />,
      onClick: (screen: Screen) => setUnassignScreen(screen),
      variant: "warning" as const,
      // Android only — see the matching note in Devices.tsx. A Pi is swapped
      // by editing the screen and entering the replacement's code.
      visible: (screen: Screen) =>
        canEdit && !!screen.device?.id && !screen.device?.isPi,
      tooltip: "Release the device from this screen",
    },
    {
      label: "Device History",
      icon: <ClockIcon className="h-4 w-4" />,
      onClick: (screen: Screen) => setHistoryScreen(screen),
      variant: "primary" as const,
      // Visible even with no current device — the screen timeline shows PAST
      // devices and downtime gaps too.
      visible: canView,
      tooltip: "Devices that have served this screen",
    },
    {
      label: "Device Logs",
      icon: <DocumentTextIcon className="h-4 w-4" />,
      onClick: (screen: Screen) => setLogsScreen(screen),
      variant: "primary" as const,
      // Logs belong to the current device; Android TV only.
      visible: (screen: Screen) =>
        canView && !!screen.device?.id && !screen.device?.isPi,
      tooltip: "Logs uploaded by this screen's device",
    },
    // {
    //   label: "Delete",
    //   icon: <TrashIcon className="h-4 w-4" />,
    //   onClick: handleDelete,
    //   variant: "danger" as const,
    //   visible: canDelete,
    // },
  ];

  return (
    <div className="flex flex-col">
      <div className="flex justify-end items-center mb-4">
        {canCreate && (
          <button
            onClick={() => {
              setSelectedScreen(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
            title="Create Screen"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={screens as Screen[]}
          columns={columns}
          actions={actions}
          searchable={false}
          pagination={true}
          isLoading={isScreensLoading}
          maxHeight="74vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          columnFiltersEnabled
          onColumnFilterChange={setColumnFilters}
          // filterSlot={ORIENTATION_TABS.map((tab) => {
          //   const isActive = orientation === tab.value;
          //   return (
          //     <button
          //       key={tab.value || "all"}
          //       onClick={() => setOrientation(tab.value)}
          //       title={tab.label}
          //       className={`p-2 mx-1 rounded border font-medium flex items-center transition-all duration-150 ${
          //         isActive
          //           ? "bg-primary-default text-white"
          //           : "bg-white text-primary-default border-primary-default hover:bg-primary-light hover:text-white"
          //       }`}
          //     >
          //       {tab.icon}
          //     </button>
          //   );
          // })}
          // selectable={canDelete}
          getRowId={(item) => item.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          // bulkActions={
          //   canDelete
          //     ? [
          //         {
          //           label: "Delete Selected",
          //           icon: <TrashIcon className="h-4 w-4" />,
          //           onClick: handleBulkDelete,
          //           variant: "danger" as const,
          //         },
          //       ]
          //     : undefined
          // }
        />
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title={selectedScreen ? "Edit Screen" : "Create Screen"}
          size="full"
          closeOnBackdropClick={false}
          closeOnEscape={false}
        >
          <ScreenForm
            initialData={
              selectedScreen
                ? {
                    screenName: selectedScreen.screenName,
                    debugFlag: selectedScreen.debugFlag,
                    resetFlag: selectedScreen.resetFlag,
                    status: selectedScreen.status,
                    layout: selectedScreen.layout,
                    screenSaver: selectedScreen.screenSaver,
                    address: selectedScreen.address,
                    latitude: selectedScreen.latitude,
                    longitude: selectedScreen.longitude,
                    regionId: selectedScreen.regionId,
                    region: selectedScreen.region,
                    restaurantId: selectedScreen.restaurantId,
                    tickerId: selectedScreen.tickerId,
                    widgetId: selectedScreen.widgetId,
                    primeEndTime: selectedScreen.primeEndTime,
                    primeStartTime: selectedScreen.primeStartTime,
                    orientation: selectedScreen.orientation,
                    tagIds: selectedScreen.tagIds,
                    verificationCode: selectedScreen.verificationCode || "",
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </Modal>
      )}

      {unassignScreen && unassignScreen.device?.id && (
        <UnassignDeviceModal
          isOpen={!!unassignScreen}
          onClose={() => setUnassignScreen(null)}
          deviceId={unassignScreen.device.id as string}
          screenName={unassignScreen.screenName}
          currentCode={unassignScreen.verificationCode}
        />
      )}

      {logsScreen && logsScreen.device?.id && (
        <DeviceLogsModal
          isOpen={!!logsScreen}
          onClose={() => setLogsScreen(null)}
          deviceId={logsScreen.device.id as string}
          titleContext={logsScreen.screenName}
        />
      )}

      {historyScreen && (
        <ScreenDeviceHistoryModal
          isOpen={!!historyScreen}
          onClose={() => setHistoryScreen(null)}
          screenId={historyScreen.id}
          screenName={historyScreen.screenName}
        />
      )}
    </div>
  );
};

export default Screens;
